import React from 'react';

const genId = (prefix) => `${prefix}_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

const STEP_TYPES = [
  { value: 'buttons', label: '🔘 Buttons (max 3)' },
  { value: 'list', label: '📋 List (max 8)' },
  { value: 'text', label: '✍️ Text (खुला जवाब)' },
  { value: 'message', label: '📨 Message (सिर्फ़ बताना)' },
  { value: 'udise_verify', label: '🆔 UDISE Verify (DB match)' },
];

const STATUS_TAGS = [
  { value: '', label: '— वही रखें —' },
  { value: 'new', label: 'New Lead' },
  { value: 'partner', label: 'Partner' },
  { value: 'support', label: 'Support' },
  { value: 'complaint', label: 'Complaint' },
];

/**
 * Simple flow builder for a single conversation flow.
 * Props: flow = { final_message, steps: [...] }, onChange(newFlow)
 */
export const WhatsAppFlowBuilder = ({ flow, onChange }) => {
  const steps = flow?.steps || [];

  const update = (patch) => onChange({ ...flow, ...patch });
  const updateStep = (sidx, patch) =>
    update({ steps: steps.map((s, i) => (i === sidx ? { ...s, ...patch } : s)) });

  const addStep = () => {
    update({
      steps: [...steps, {
        id: genId('step'), type: 'buttons', key: '', prompt: 'नया सवाल यहाँ लिखें?',
        button: 'चुनें', set_status: '',
        options: [{ id: genId('opt'), title: 'विकल्प 1', description: '', solution: '', next: 'FINAL', set_status: '' }],
        next: 'FINAL', confirm_prompt: '', not_found_text: '', yes_title: '', no_title: '',
        verified_next: 'FINAL', failed_next: 'FINAL',
      }],
    });
  };
  const removeStep = (sidx) => update({ steps: steps.filter((_, i) => i !== sidx) });
  const moveStep = (sidx, dir) => {
    const tgt = sidx + dir;
    if (tgt < 0 || tgt >= steps.length) return;
    const next = [...steps];
    [next[sidx], next[tgt]] = [next[tgt], next[sidx]];
    update({ steps: next });
  };

  const addOption = (sidx) => {
    const s = steps[sidx];
    const max = s.type === 'buttons' ? 3 : 8;
    if ((s.options || []).length >= max) return;
    updateStep(sidx, { options: [...(s.options || []), { id: genId('opt'), title: 'नया विकल्प', description: '', solution: '', next: 'FINAL', set_status: '' }] });
  };
  const removeOption = (sidx, oidx) => updateStep(sidx, { options: steps[sidx].options.filter((_, i) => i !== oidx) });
  const updateOption = (sidx, oidx, field, value) =>
    updateStep(sidx, { options: steps[sidx].options.map((o, i) => (i === oidx ? { ...o, [field]: value } : o)) });

  const nextChoices = (selfId) => [
    { value: 'FINAL', label: '✅ FINAL — अंतिम जवाब' },
    { value: 'MENU', label: '🏠 MENU — मुख्य menu दिखाएँ' },
    ...steps.filter((s) => s.id !== selfId).map((s) => ({ value: s.id, label: `➡️ Step ${steps.indexOf(s) + 1}: ${(s.prompt || '').slice(0, 24)}` })),
  ];

  const NextSelect = ({ value, onChange: oc, selfId, testid }) => (
    <select value={value || 'FINAL'} onChange={(e) => oc(e.target.value)}
      className="w-full px-2 py-1.5 border rounded-md text-xs bg-white" data-testid={testid}>
      {nextChoices(selfId).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
    </select>
  );

  return (
    <div className="space-y-3" data-testid="flow-builder">
      <div className="bg-white rounded-lg p-3 border">
        <label className="block text-xs font-semibold text-gray-600 mb-1">🎯 अंतिम जवाब (Final message) — FINAL पर पहुँचने पर जाएगा</label>
        <textarea value={flow?.final_message || ''} rows={2} onChange={(e) => update({ final_message: e.target.value })}
          className="w-full px-3 py-2 border rounded-lg text-sm resize-none" placeholder="(optional) धन्यवाद! ..." data-testid="flow-final-message" />
      </div>

      {steps.map((step, sidx) => {
        const isInteractive = step.type === 'buttons' || step.type === 'list';
        const isMessage = step.type === 'message';
        const isUdise = step.type === 'udise_verify';
        const max = step.type === 'buttons' ? 3 : 8;
        return (
          <div key={step.id} className="bg-indigo-50/50 rounded-lg p-3 border border-indigo-100 space-y-3" data-testid={`flow-step-${sidx}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-indigo-700">Step {sidx + 1}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => moveStep(sidx, -1)} disabled={sidx === 0} className="text-gray-400 hover:text-gray-700 disabled:opacity-30 text-xs" data-testid={`flow-step-up-${sidx}`}><i className="fa-solid fa-arrow-up"></i></button>
                <button onClick={() => moveStep(sidx, 1)} disabled={sidx === steps.length - 1} className="text-gray-400 hover:text-gray-700 disabled:opacity-30 text-xs" data-testid={`flow-step-down-${sidx}`}><i className="fa-solid fa-arrow-down"></i></button>
                <button onClick={() => removeStep(sidx)} className="text-red-400 hover:text-red-600 text-xs" data-testid={`flow-step-remove-${sidx}`}><i className="fa-solid fa-trash-can"></i></button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-medium text-gray-500 mb-1">सवाल का प्रकार</label>
                <select value={step.type} onChange={(e) => updateStep(sidx, { type: e.target.value })} className="w-full px-2.5 py-2 border rounded-lg text-sm bg-white" data-testid={`flow-step-type-${sidx}`}>
                  {STEP_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-500 mb-1">CRM Label (जवाब सेव नाम)</label>
                <input value={step.key || ''} onChange={(e) => updateStep(sidx, { key: e.target.value })} className="w-full px-2.5 py-2 border rounded-lg text-sm" placeholder="जैसे: स्कूल" data-testid={`flow-step-key-${sidx}`} />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-medium text-gray-500 mb-1">{isUdise ? 'Code माँगने का message' : isMessage ? 'Message (text)' : 'सवाल (Prompt)'}</label>
              <textarea value={step.prompt || ''} rows={2} onChange={(e) => updateStep(sidx, { prompt: e.target.value })} className="w-full px-2.5 py-2 border rounded-lg text-sm resize-none" data-testid={`flow-step-prompt-${sidx}`} />
            </div>

            {step.type === 'list' && (
              <div>
                <label className="block text-[11px] font-medium text-gray-500 mb-1">List बटन का नाम (max 20)</label>
                <input value={step.button || ''} maxLength={20} onChange={(e) => updateStep(sidx, { button: e.target.value })} className="w-full px-2.5 py-2 border rounded-lg text-sm" data-testid={`flow-step-button-${sidx}`} />
              </div>
            )}

            {/* UDISE verify fields */}
            {isUdise && (
              <div className="space-y-2 bg-white rounded-lg p-2.5 border">
                <input value={step.confirm_prompt || ''} onChange={(e) => updateStep(sidx, { confirm_prompt: e.target.value })} className="w-full px-2.5 py-1.5 border rounded-md text-xs" placeholder="Confirm message (record मिलने पर)" data-testid={`flow-udise-confirm-${sidx}`} />
                <input value={step.not_found_text || ''} onChange={(e) => updateStep(sidx, { not_found_text: e.target.value })} className="w-full px-2.5 py-1.5 border rounded-md text-xs" placeholder="Record न मिलने पर message" data-testid={`flow-udise-notfound-${sidx}`} />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] text-gray-400 mb-0.5">✅ Verify होने पर →</label>
                    <NextSelect value={step.verified_next} selfId={step.id} onChange={(v) => updateStep(sidx, { verified_next: v })} testid={`flow-udise-verified-next-${sidx}`} />
                  </div>
                  <div>
                    <label className="block text-[10px] text-gray-400 mb-0.5">❌ न मिलने पर →</label>
                    <NextSelect value={step.failed_next} selfId={step.id} onChange={(v) => updateStep(sidx, { failed_next: v })} testid={`flow-udise-failed-next-${sidx}`} />
                  </div>
                </div>
              </div>
            )}

            {/* Options for buttons/list */}
            {isInteractive && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-gray-600">विकल्प ({(step.options || []).length}/{max})</span>
                  <button onClick={() => addOption(sidx)} disabled={(step.options || []).length >= max} className="text-xs px-2 py-1 bg-indigo-600 text-white rounded-md disabled:opacity-40" data-testid={`flow-add-option-${sidx}`}><i className="fa-solid fa-plus"></i> विकल्प</button>
                </div>
                {(step.options || []).map((opt, oidx) => (
                  <div key={opt.id} className="bg-white rounded-lg p-2.5 border space-y-2" data-testid={`flow-step-${sidx}-option-${oidx}`}>
                    <div className="flex items-center gap-2">
                      <input value={opt.title} maxLength={24} onChange={(e) => updateOption(sidx, oidx, 'title', e.target.value)} className="flex-1 px-2.5 py-1.5 border rounded-md text-sm" placeholder="विकल्प title" data-testid={`flow-option-title-${sidx}-${oidx}`} />
                      <button onClick={() => removeOption(sidx, oidx)} className="text-red-400 hover:text-red-600 text-xs" data-testid={`flow-option-remove-${sidx}-${oidx}`}><i className="fa-solid fa-xmark"></i></button>
                    </div>
                    {step.type === 'list' && (
                      <input value={opt.description || ''} maxLength={72} onChange={(e) => updateOption(sidx, oidx, 'description', e.target.value)} className="w-full px-2.5 py-1.5 border rounded-md text-xs" placeholder="छोटा विवरण (optional)" data-testid={`flow-option-desc-${sidx}-${oidx}`} />
                    )}
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="block text-[10px] text-gray-400 mb-0.5">इसके बाद →</label>
                        <NextSelect value={opt.next} selfId={step.id} onChange={(v) => updateOption(sidx, oidx, 'next', v)} testid={`flow-option-next-${sidx}-${oidx}`} />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-400 mb-0.5">CRM tag</label>
                        <select value={opt.set_status || ''} onChange={(e) => updateOption(sidx, oidx, 'set_status', e.target.value)} className="w-full px-2 py-1.5 border rounded-md text-xs bg-white" data-testid={`flow-option-status-${sidx}-${oidx}`}>
                          {STATUS_TAGS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-400 mb-0.5">Solution</label>
                        <input value={opt.solution || ''} onChange={(e) => updateOption(sidx, oidx, 'solution', e.target.value)} className="w-full px-2 py-1.5 border rounded-md text-xs" placeholder="optional" data-testid={`flow-option-solution-${sidx}-${oidx}`} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Next for text / message */}
            {(step.type === 'text' || isMessage) && (
              <div>
                <label className="block text-[11px] font-medium text-gray-500 mb-1">इसके बाद →</label>
                <NextSelect value={step.next} selfId={step.id} onChange={(v) => updateStep(sidx, { next: v })} testid={`flow-step-next-${sidx}`} />
              </div>
            )}
          </div>
        );
      })}

      <button onClick={addStep} className="w-full py-2.5 border-2 border-dashed border-indigo-300 text-indigo-600 rounded-lg text-sm font-medium hover:bg-indigo-50 flex items-center justify-center gap-2" data-testid="flow-add-step-btn">
        <i className="fa-solid fa-plus"></i> नया Step जोड़ें
      </button>
      <p className="text-[11px] text-gray-400 text-center">🔙 "पीछे" और 🏠 "मुख्य Menu" बटन हर list step पर अपने-आप जुड़ते हैं — user कभी फँसेगा नहीं।</p>
    </div>
  );
};

export default WhatsAppFlowBuilder;
